<template>
  <div class="wizard-body columns content-box small-9">
    <form @submit.prevent="submit">
      <div class="table-header">
        <div>
          <h4>{{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.INBOXES.TITLE') }}</h4>
          <p class="table-subtitle">
            {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.INBOXES.DESC') }}
          </p>
        </div>
        <div class="header-actions">
          <span class="selected-count">
            {{ selectedInboxIds.length }}
            {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.INBOXES.SELECTED_COUNT') }}
          </span>
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            @click.prevent="toggleAll"
          >
            {{
              allSelected
                ? $t('OPPORTUNITY_PIPELINES.ACTIONS.UNSELECT_ALL')
                : $t('OPPORTUNITY_PIPELINES.ACTIONS.SELECT_ALL')
            }}
          </woot-button>
        </div>
      </div>

      <woot-loading-state
        v-if="isFetching"
        :message="$t('OPPORTUNITY_PIPELINES.CREATE_FLOW.INBOXES.LOADING')"
      />

      <div v-else class="table-wrap">
        <table class="woot-table selection-table">
          <thead>
            <tr>
              <th class="check-col" />
              <th>{{ $t('OPPORTUNITY_PIPELINES.INBOXES.NAME') }}</th>
              <th>{{ $t('OPPORTUNITY_PIPELINES.INBOXES.TYPE') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="inbox in inboxes" :key="inbox.id">
              <td class="check-col">
                <input
                  :id="`inbox-${inbox.id}`"
                  v-model="selectedInboxIds"
                  class="row-checkbox"
                  type="checkbox"
                  :value="inbox.id"
                />
              </td>
              <td>
                <label :for="`inbox-${inbox.id}`" class="row-label">
                  {{ inbox.name }}
                </label>
              </td>
              <td>{{ prettyChannelType(inbox.channel_type) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="button-row">
        <woot-button
          type="button"
          color-scheme="secondary"
          @click="goToPrevious"
        >
          {{ $t('OPPORTUNITY_PIPELINES.ACTIONS.BACK') }}
        </woot-button>
        <woot-button :is-loading="isSubmitting">
          {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.INBOXES.SUBMIT') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import router from '../../../../index';
import { patchDraft, readDraft } from './draftState';

export default {
  mixins: [alertMixin],
  data() {
    return {
      selectedInboxIds: [],
      currentInboxIds: [],
      isSubmitting: false,
      isFetching: false,
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
    }),
    pipelineId() {
      return this.$route.params.pipelineId;
    },
    isNewDraftFlow() {
      return this.$route.name === 'settings_opportunity_pipelines_new_inboxes';
    },
    allSelected() {
      return (
        this.inboxes.length > 0 &&
        this.selectedInboxIds.length === this.inboxes.length
      );
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    prettyChannelType(channelType = '') {
      return channelType.replace('Channel::', '');
    },
    async fetchData() {
      this.isFetching = true;
      try {
        const requests = [this.$store.dispatch('inboxes/get')];

        if (!this.isNewDraftFlow) {
          requests.push(
            this.$store.dispatch(
              'opportunityPipelines/getInboxes',
              this.pipelineId
            )
          );
        }

        const [, selectedInboxes] = await Promise.all(requests);

        if (this.isNewDraftFlow) {
          const draft = readDraft();
          this.selectedInboxIds = draft.inboxes || [];
        } else {
          this.selectedInboxIds = (selectedInboxes || []).map(
            item => item.inbox_id
          );
        }

        this.currentInboxIds = [...this.selectedInboxIds];
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_INBOXES'));
      } finally {
        this.isFetching = false;
      }
    },
    toggleAll() {
      if (this.allSelected) {
        this.selectedInboxIds = [];
        return;
      }
      this.selectedInboxIds = this.inboxes.map(inbox => inbox.id);
    },
    async submit() {
      if (this.isNewDraftFlow) {
        patchDraft({ inboxes: [...this.selectedInboxIds] });
        router.replace({ name: 'settings_opportunity_pipelines_new_stages' });
        return;
      }

      this.isSubmitting = true;
      try {
        const toAdd = this.selectedInboxIds.filter(
          id => !this.currentInboxIds.includes(id)
        );
        const toRemove = this.currentInboxIds.filter(
          id => !this.selectedInboxIds.includes(id)
        );

        const requests = [];

        if (toAdd.length) {
          requests.push(
            this.$store.dispatch('opportunityPipelines/saveInboxes', {
              pipelineId: this.pipelineId,
              inboxIds: toAdd,
            })
          );
        }

        if (toRemove.length) {
          requests.push(
            this.$store.dispatch('opportunityPipelines/removeInboxes', {
              pipelineId: this.pipelineId,
              inboxIds: toRemove,
            })
          );
        }

        await Promise.all(requests);

        this.currentInboxIds = [...this.selectedInboxIds];
        router.replace({
          name: 'settings_opportunity_pipelines_stages',
          params: { pipelineId: this.pipelineId },
        });
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.SAVE_INBOXES'));
      } finally {
        this.isSubmitting = false;
      }
    },
    goToPrevious() {
      if (this.isNewDraftFlow) {
        router.replace({ name: 'settings_opportunity_pipelines_new_users' });
        return;
      }

      router.replace({
        name: 'settings_opportunity_pipelines_users',
        params: { pipelineId: this.pipelineId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.table-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.table-subtitle {
  color: var(--s-700);
  font-size: var(--font-size-small);
  line-height: 1.6;
  margin: 0;
}

.header-actions {
  align-items: center;
  display: flex;
  gap: var(--space-small);
}

.selected-count {
  color: var(--s-700);
  font-size: var(--font-size-small);
}

.table-wrap {
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-medium);
  margin-top: var(--space-small);
  max-height: 48rem;
  overflow: auto;
}

.check-col {
  width: 4rem;
}

.selection-table {
  margin-bottom: 0;
}

.selection-table tbody tr {
  transition: background-color 0.12s ease;
}

.selection-table tbody tr:hover {
  background: var(--s-25);
}

.selection-table td,
.selection-table th {
  line-height: 1.5;
  vertical-align: middle;
}

.row-checkbox {
  accent-color: var(--w-500);
  cursor: pointer;
  margin: 0;
  vertical-align: middle;
}

.row-label {
  cursor: pointer;
  margin: 0;
}

.button-row {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  margin-top: var(--space-small);
}

@media screen and (max-width: 768px) {
  .table-header {
    flex-direction: column;
    gap: var(--space-small);
  }

  .header-actions {
    justify-content: space-between;
    width: 100%;
  }
}
</style>
